/**
 * Camera stream helpers for the membership barcode scanner.
 *
 * Android WebView (and some Chrome Android builds) reject ZXing's default
 * `{ facingMode: 'environment' }` constraint, and also deny getUserMedia when
 * it is not started from a user gesture. Call {@link requestBarcodeCameraStream}
 * from the click that opens the scanner, then attach the stream with
 * `decodeFromStream`.
 *
 * The stream itself decides whether a barcode is readable at all: the browser
 * default is a 640x480 auto-focus-free feed off whichever rear lens the OS
 * picks, which leaves a printed membership label a few pixels wide. The request
 * below asks for a high-resolution focused feed and degrades one step at a time
 * so older devices still get a stream. If the lens it lands on turns out to be
 * one that cannot focus close up, it swaps afterwards — never before, because
 * the first getUserMedia has to stay inside the opening gesture.
 */

/** `focusMode` and `torch` are camera capabilities the TS DOM lib does not model yet. */
interface BarcodeTrackConstraintSet extends MediaTrackConstraintSet {
  focusMode?: string;
  torch?: boolean;
}

interface BarcodeTrackCapabilities extends MediaTrackCapabilities {
  torch?: boolean;
}

const PREFERRED_CAPTURE_SIZE = { width: 1920, height: 1080 };
const FALLBACK_CAPTURE_SIZE = { width: 1280, height: 720 };

/** Matches the rear-facing entries `enumerateDevices` reports once labels are readable. */
const REAR_LENS_LABEL = /back|rear|environment/i;

/**
 * Rear modules that cannot hold focus on a label at arm's length. `facingMode:
 * 'environment'` is free to resolve to any of them, and an ultra-wide picks a
 * barcode up as a handful of blurry pixels.
 */
const UNFOCUSABLE_LENS_LABEL = /ultra|telephoto|depth|infrared/i;

/**
 * Failures that retrying with looser constraints cannot fix. Walking the whole
 * ladder for these would fire several pointless camera requests and delay the
 * message the user needs to see.
 */
const NON_RECOVERABLE_CAMERA_ERRORS = new Set([
  'NotAllowedError',
  'PermissionDeniedError',
  'SecurityError',
  'NotReadableError',
  'AbortError',
]);

export function isCameraApiAvailable(): boolean {
  return (
    typeof navigator !== 'undefined' && typeof navigator.mediaDevices?.getUserMedia === 'function'
  );
}

export function isSecureCameraContext(): boolean {
  return typeof window === 'undefined' || window.isSecureContext !== false;
}

export function isCameraPermissionDenied(error: unknown): boolean {
  const name = getErrorName(error);
  return name === 'NotAllowedError' || name === 'PermissionDeniedError';
}

export function stopMediaStream(stream: MediaStream | null | undefined): void {
  stream?.getTracks().forEach(track => {
    try {
      track.stop();
    } catch {
      // Ignore track-stop failures while tearing down the camera.
    }
  });
}

export async function requestBarcodeCameraStream(): Promise<MediaStream> {
  if (!isCameraApiAvailable()) {
    throw Object.assign(new Error('CAMERA_UNAVAILABLE'), { name: 'NotSupportedError' });
  }

  if (!isSecureCameraContext()) {
    throw Object.assign(new Error('CAMERA_INSECURE_CONTEXT'), { name: 'SecurityError' });
  }

  // Nothing may be awaited before the first getUserMedia: Android WebView only
  // honours the request while it is still inside the opening tap's gesture.
  const stream = await openCameraStream(null);
  const betterLens = await findBetterRearCameraDeviceId(stream);

  if (betterLens) {
    const swapped = await swapToLens(stream, betterLens);
    await applyContinuousFocus(swapped);
    return swapped;
  }

  await applyContinuousFocus(stream);
  return stream;
}

async function openCameraStream(deviceId: string | null): Promise<MediaStream> {
  let lastError: unknown;

  for (const constraints of buildCameraConstraints(deviceId)) {
    try {
      return await navigator.mediaDevices.getUserMedia(constraints);
    } catch (error) {
      lastError = error;
      if (NON_RECOVERABLE_CAMERA_ERRORS.has(getErrorName(error))) {
        throw error;
      }
    }
  }

  throw lastError ?? Object.assign(new Error('CAMERA_UNAVAILABLE'), { name: 'NotFoundError' });
}

/** Falls back to the stream we already hold if the preferred lens will not open. */
async function swapToLens(current: MediaStream, deviceId: string): Promise<MediaStream> {
  try {
    const replacement = await openCameraStream(deviceId);
    stopMediaStream(current);
    return replacement;
  } catch {
    return current;
  }
}

/**
 * Progressively relaxed constraints for a given lens.
 *
 * When a specific `deviceId` is requested every entry stays pinned to it —
 * relaxing down to `facingMode` there would just reopen the lens we are trying
 * to move away from. The unpinned ladder ends on the pre-existing constraints
 * so a device that rejects resolution or focus hints still gets a camera.
 */
function buildCameraConstraints(deviceId: string | null): MediaStreamConstraints[] {
  const lens: MediaTrackConstraints = deviceId
    ? { deviceId: { exact: deviceId } }
    : { facingMode: { ideal: 'environment' } };

  const ladder: MediaStreamConstraints[] = [
    {
      audio: false,
      video: {
        ...lens,
        width: { ideal: PREFERRED_CAPTURE_SIZE.width },
        height: { ideal: PREFERRED_CAPTURE_SIZE.height },
        advanced: [{ focusMode: 'continuous' } as BarcodeTrackConstraintSet],
      },
    },
    {
      audio: false,
      video: {
        ...lens,
        width: { ideal: FALLBACK_CAPTURE_SIZE.width },
        height: { ideal: FALLBACK_CAPTURE_SIZE.height },
      },
    },
  ];

  if (deviceId) {
    return ladder;
  }

  return [
    ...ladder,
    { audio: false, video: { facingMode: { ideal: 'environment' } } },
    { audio: false, video: true },
  ];
}

/**
 * Looks for a rear lens that can focus close up, but only once we hold a stream
 * whose label shows the OS handed us one that cannot. Enumerating first would
 * be wasteful and, more importantly, cannot happen before `getUserMedia`.
 *
 * Returns null whenever labels are unreadable — they stay empty until camera
 * permission is granted — which leaves the original stream in place.
 */
async function findBetterRearCameraDeviceId(stream: MediaStream): Promise<string | null> {
  if (typeof navigator.mediaDevices?.enumerateDevices !== 'function') {
    return null;
  }

  const track = getVideoTrack(stream);
  const currentLabel = track?.label ?? '';
  if (!currentLabel || !UNFOCUSABLE_LENS_LABEL.test(currentLabel)) {
    return null;
  }

  try {
    const currentDeviceId = track?.getSettings?.().deviceId;
    const devices = await navigator.mediaDevices.enumerateDevices();
    const focusable = devices.find(
      device =>
        device.kind === 'videoinput' &&
        REAR_LENS_LABEL.test(device.label) &&
        !UNFOCUSABLE_LENS_LABEL.test(device.label) &&
        device.deviceId !== currentDeviceId
    );
    return focusable?.deviceId || null;
  } catch {
    return null;
  }
}

/**
 * Re-applies continuous autofocus after the stream exists. Some Android builds
 * accept `focusMode` here but reject it inside `getUserMedia`.
 */
async function applyContinuousFocus(stream: MediaStream): Promise<void> {
  const track = getVideoTrack(stream);
  if (typeof track?.applyConstraints !== 'function') {
    return;
  }

  try {
    await track.applyConstraints({
      advanced: [{ focusMode: 'continuous' } as BarcodeTrackConstraintSet],
    });
  } catch {
    // Focus control is optional; a fixed-focus camera still scans.
  }
}

export function isTorchSupported(stream: MediaStream | null | undefined): boolean {
  const track = getVideoTrack(stream);
  if (typeof track?.getCapabilities !== 'function') {
    return false;
  }

  try {
    return (track.getCapabilities() as BarcodeTrackCapabilities).torch === true;
  } catch {
    return false;
  }
}

/**
 * Toggles the torch directly on the track. ZXing's own `switchTorch` goes
 * through `setPhotoOptions`, which throws on Chrome Android during teardown.
 *
 * @returns whether the torch is on after the call.
 */
export async function setTorchEnabled(
  stream: MediaStream | null | undefined,
  enabled: boolean
): Promise<boolean> {
  const track = getVideoTrack(stream);
  if (typeof track?.applyConstraints !== 'function') {
    return false;
  }

  try {
    await track.applyConstraints({ advanced: [{ torch: enabled } as BarcodeTrackConstraintSet] });
    return enabled;
  } catch {
    return false;
  }
}

function getVideoTrack(stream: MediaStream | null | undefined): MediaStreamTrack | undefined {
  return stream?.getVideoTracks?.()[0];
}

export function getCameraAccessErrorMessage(error: unknown): string {
  if (
    getErrorName(error) === 'NotSupportedError' ||
    getErrorMessage(error) === 'CAMERA_UNAVAILABLE'
  ) {
    return 'Camera access is not available. Upload a barcode image instead.';
  }

  if (
    getErrorName(error) === 'SecurityError' ||
    getErrorMessage(error) === 'CAMERA_INSECURE_CONTEXT'
  ) {
    return 'Camera needs a secure page. Take a photo or upload a barcode image.';
  }

  if (isCameraPermissionDenied(error)) {
    return 'Camera permission was blocked. Tap Try camera again, or take a photo of the barcode.';
  }

  if (getErrorName(error) === 'NotFoundError') {
    return 'No camera was found on this device. Take a photo or upload a barcode image.';
  }

  if (getErrorName(error) === 'NotReadableError') {
    return 'The camera is already in use. Close other camera apps and tap Try camera again.';
  }

  return 'Could not access the camera. Take a photo or upload a barcode image.';
}

function getErrorName(error: unknown): string {
  if (error && typeof error === 'object' && 'name' in error && typeof error.name === 'string') {
    return error.name;
  }
  return '';
}

function getErrorMessage(error: unknown): string {
  if (
    error &&
    typeof error === 'object' &&
    'message' in error &&
    typeof error.message === 'string'
  ) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  return '';
}
